import { cva, type VariantProps } from 'class-variance-authority';
import { type HTMLAttributes, type ReactNode } from 'react';

import { cn } from '@/lib/utils';

/**
 * Tag do Design System (ADR-0065): retângulo reto de 11px com tracking 0.02em e
 * respiro 3px/10px, vindo do `.tag` do pacote. A cor de sinal pericial (ok /
 * warn / bad) fica reservada a resultado e direção — nunca decorativa — e o
 * chip **sempre** carrega texto: cor não pode ser o único portador de
 * significado (regra do pacote, e o que o `StatusPill` garante).
 */
const badgeVariants = cva(
  'inline-flex items-center gap-1 border px-[10px] py-[3px] text-[11px] font-medium tracking-[0.02em]',
  {
    variants: {
      variant: {
        neutral: 'border-border bg-surface-2 text-fg-muted',
        info: 'border-accent-200 bg-accent-bg text-accent-800',
        success: 'border-success/30 bg-success-50 text-success-700',
        warning: 'border-warning/30 bg-warning-50 text-warning-700',
        danger: 'border-danger/30 bg-danger-50 text-danger-700',
        /** `tag-outline` do pacote: contorno em aço sem preenchimento. */
        outline: 'border-accent bg-transparent text-accent-700',
        /*
         * Boxes de situação do caso para o cliente (HTML 6.8, E165 — ADR-0114 §5): aguardando
         * documentos, em avaliação, entrega disponível e falha dita. Não são o sinal pericial
         * acima: dizem em que pé está o pedido, nunca o que o exame concluiu. 12px com 5px/9px de
         * respiro, como o `.badge` do molde; o canto segue reto (ADR-0065).
         */
        attention:
          'border-situacao-attention-border bg-situacao-attention-bg px-[9px] py-[5px] text-[12px] leading-[1.5] text-situacao-attention-fg',
        progress:
          'border-situacao-progress-border bg-situacao-progress-bg px-[9px] py-[5px] text-[12px] leading-[1.5] text-situacao-progress-fg',
        ok: 'border-situacao-ok-border bg-situacao-ok-bg px-[9px] py-[5px] text-[12px] leading-[1.5] text-situacao-ok-fg',
        error:
          'border-situacao-error-border bg-situacao-error-bg px-[9px] py-[5px] text-[12px] leading-[1.5] text-situacao-error-fg',
      },
    },
    defaultVariants: {
      variant: 'neutral',
    },
  },
);

export interface BadgeProps
  extends HTMLAttributes<HTMLSpanElement>, VariantProps<typeof badgeVariants> {
  icon?: ReactNode;
}

export function Badge({ className, variant, icon, children, ...props }: BadgeProps) {
  return (
    <span className={cn(badgeVariants({ variant, className }))} {...props}>
      {icon ? <span aria-hidden>{icon}</span> : null}
      {children}
    </span>
  );
}

export { badgeVariants };
